// src/pages/ChatBot/DocumentAccessModal.tsx
//
// Role-scoping editor for a chatbot document. Renders the tenant's RBAC
// roles (fetched once by DocumentsPanel) as a multi-select checkbox list:
// the checked roles are the ones the document is scoped to, and an empty
// selection means the document is available to every chatbot user. Uses the
// shared .smrtdocs-modal shell so it matches the other admin dialogs.

import { useState } from 'react';
import { coversAllRoles } from './document-roles';
import './DocumentAccessModal.css';

export type OrgRole = {
  id: string;
  name: string;
  description?: string;
  isSystem?: boolean;
  isDefault?: boolean;
};

export default function DocumentAccessModal({
  fileName,
  roles,
  initialRoleIDs,
  onSave,
  onClose,
}: {
  fileName: string;
  roles: OrgRole[];
  initialRoleIDs: string[];
  onSave: (roleIDs: string[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(initialRoleIDs);

  const toggleRole = (roleID: string) => {
    setSelected((ids) =>
      ids.includes(roleID) ? ids.filter((id) => id !== roleID) : [...ids, roleID],
    );
  };

  const summary =
    selected.length === 0
      ? 'Open — everyone'
      : coversAllRoles(selected, roles)
        ? 'All roles — open to everyone'
        : `${selected.length} role${selected.length === 1 ? '' : 's'}`;

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: '520px' }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Document Access</h2>
        </div>
        <div className="smrtdocs-modal-body">
          <p className="mcam-filename">{fileName}</p>
          <p className="mcam-note">
            Select the roles this document is scoped to — the chatbot will only
            use it to answer users holding at least one selected role. With no
            roles selected, the document is available to all chatbot users.
          </p>

          {roles.length === 0 ? (
            <p className="mcam-empty">
              No roles are defined for this organization yet. Roles can be
              created from the Users page.
            </p>
          ) : (
            <ul className="mcam-role-list">
              {roles.map((role) => (
                <li key={role.id}>
                  <label className="mcam-role-row" title={role.description || ''}>
                    <input
                      type="checkbox"
                      checked={selected.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span className="mcam-role-name">{role.name}</span>
                    {role.isSystem && <span className="mcam-role-tag">System</span>}
                    {role.isDefault && <span className="mcam-role-tag">Default</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}

          <p className="mcam-summary">{summary}</p>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn-save" onClick={() => onSave(selected)}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
